(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(require('./ai-chat-core.js'), null);
    return;
  }

  if (root) {
    root.MarineMaxAIChatHubSpotForm = factory(root.MarineMaxAIChatCore, root);
  }
})(typeof window !== 'undefined' ? window : null, function (core, root) {
  'use strict';

  if (!core) {
    throw new Error('MarineMax AI Chat core is required by the HubSpot form module');
  }

  const fieldErrors = Object.freeze({
    consent: 'Please accept the Privacy Policy.',
    email: 'Please enter an email address.',
    emailInvalid: 'Please enter a valid email address.',
    firstname: 'Please enter a first name.',
    lastname: 'Please enter a last name.',
    zip: 'Please enter a ZIP code.',
  });

  const statusMessages = Object.freeze({
    configuration: 'HubSpot form is not configured yet.',
    submission: 'Something went wrong sending your request. Please try again.',
  });

  const diagnosticPrefix = '[MarineMax AI Chat][HubSpot]';
  const summaryFieldPattern = /(?:^|_)(?:ai_)?(?:summary|transcript|chat_history|conversation)(?:_|$)/i;

  const sourceByDomain = new Map([
    ['marinemax.com', 'MarineMax.com'],
    ['skipperbuds.com', 'SkipperBuds.com'],
    ['marinemaxyachts.com', 'MarineMaxYachts.com'],
  ]);

  function normalizeSourceDomain(value) {
    const domain = String(value || '').trim();
    if (!domain) return '';
    try {
      const url = new URL(domain.includes('://') ? domain : `https://${domain}`);
      return url.hostname.toLowerCase().replace(/^www\./, '').replace(/\.$/, '');
    } catch (_) {
      return '';
    }
  }

  function isDiagnosticsEnabled(windowRef, explicitValue) {
    if (explicitValue !== undefined) return explicitValue === true;
    if (windowRef && windowRef.__MARINEMAX_AI_CHAT_DEBUG__ === true) return true;

    const location = (windowRef && windowRef.location) || {};
    const href = String(location.href || '');
    const search = String(
      location.search || (href.includes('?') ? href.slice(href.indexOf('?')) : ''),
    );
    return /(?:^|[?&])ai_chat_debug=(?:1|true)(?:&|$)/i.test(search);
  }

  function createHubSpotFormController(options = {}) {
    const chatRoot = options.root;
    const client = options.client;
    const analytics = options.analytics;
    const navigation = options.navigation;
    const conversation = options.conversation;
    const messages = options.messages;
    const resizeInput = options.resizeInput || (() => {});
    const isDesktopFormOnly = options.isDesktopFormOnly || (() => false);
    const onReturnToPdp = options.onReturnToPdp || (() => false);
    const stockNumber = String(options.stockNumber || '');
    const productTitle = String(options.productTitle || '');
    const boatBrand = String(options.boatBrand || '').trim();
    const boatModel = String(options.boatModel || '').trim();
    const defaultInputPlaceholder = String(options.defaultInputPlaceholder || '');
    const portalId = String(
      options.portalId || (chatRoot && chatRoot.dataset.hubspotPortalId) || '',
    ).trim();
    const formId = String(
      options.formId || (chatRoot && chatRoot.dataset.hubspotFormId) || '',
    ).trim();
    const configuredRegion = String(options.region || chatRoot?.dataset.hubspotRegion || 'na1').trim().toLowerCase();
    const region = /^[a-z]{2}\d+$/.test(configuredRegion) ? configuredRegion : 'na1';
    const windowRef = options.window || root;
    const documentRef =
      options.document ||
      (chatRoot && chatRoot.ownerDocument) ||
      (windowRef && windowRef.document) ||
      null;
    const createFormData =
      options.createFormData ||
      (windowRef && typeof windowRef.FormData === 'function'
        ? (formElement) => new windowRef.FormData(formElement)
        : null);
    const EventClass = options.Event || (windowRef && windowRef.Event);
    const logger = options.logger || (windowRef && windowRef.console);
    const matchMedia =
      options.matchMedia ||
      (windowRef && typeof windowRef.matchMedia === 'function'
        ? windowRef.matchMedia.bind(windowRef)
        : null);

    if (!chatRoot || !documentRef || !windowRef) {
      throw new Error('AI Chat HubSpot form DOM dependencies are incomplete');
    }
    if (!client || typeof client.submit !== 'function') {
      throw new Error('A HubSpot client is required by the HubSpot form module');
    }
    if (!analytics || typeof analytics.pushLeadEvents !== 'function') {
      throw new Error('AI Chat analytics is required by the HubSpot form module');
    }
    if (
      !navigation ||
      ['showChat', 'showExpert', 'showHubspotSuccess'].some(
        (methodName) => typeof navigation[methodName] !== 'function',
      )
    ) {
      throw new Error('AI Chat navigation is required by the HubSpot form module');
    }
    if (!conversation || typeof conversation.setConversationId !== 'function') {
      throw new Error('AI Chat conversation is required by the HubSpot form module');
    }
    if (!messages || typeof messages.clear !== 'function') {
      throw new Error('AI Chat messages are required by the HubSpot form module');
    }
    if (typeof createFormData !== 'function') {
      throw new Error('FormData is required by the HubSpot form module');
    }

    const formContainer = chatRoot.querySelector('.ai-chat__hubspot-form');
    const form = formContainer
      ? formContainer.querySelector('.ai-chat__lead-form')
      : null;
    const status = formContainer
      ? formContainer.querySelector('[data-ai-chat-form-status]')
      : null;
    const embed = chatRoot.querySelector('[data-ai-chat-hubspot-embed]');
    const embedLoader = embed?.querySelector('[data-ai-chat-hubspot-loader]');
    const embedBaseInstanceId = embed?.querySelector('.hs-form-frame')?.dataset.instanceId || chatRoot.id || 'sales-contact-form';
    let embedStarted = false;
    let embedCompleted = false;
    let embedRestartCount = 0;
    let embedInstanceId = null;
    let embedTimer = null;
    const stickySubmit = chatRoot.querySelector('[data-ai-chat-sticky-submit]');
    const backToChatButton = chatRoot.querySelector('.ai-chat__back-to-chat');
    const continueButton = chatRoot.querySelector('.ai-chat__splash-continue');
    const phoneCountrySelect = chatRoot.querySelector('[name="phone_country"]');
    const phoneInput = chatRoot.querySelector('[name="phone"]');
    const input = chatRoot.querySelector('.ai-chat__input textarea');
    const listenerDisposers = [];
    let desktopFormOnlyWasActive = false;
    let initialized = false;
    let submitInFlight = false;
    let returnTo = 'chat';

    function writeDiagnostics(eventName, details, level = 'info') {
      if (!isDiagnosticsEnabled(windowRef, options.debug) || !logger) return false;
      const method = typeof logger[level] === 'function' ? logger[level] : logger.log;
      if (typeof method !== 'function') return false;
      method.call(logger, diagnosticPrefix, eventName, details);
      return true;
    }

    function getConversationDiagnostics() {
      let snapshot = [];
      try {
        if (
          messages.store &&
          typeof messages.store.snapshot === 'function'
        ) {
          const storedMessages = messages.store.snapshot();
          if (Array.isArray(storedMessages)) snapshot = storedMessages;
        }
      } catch (_) {}

      return {
        conversationId: conversation.conversationId || null,
        messageCharacterCount: snapshot.reduce(
          (total, message) => total + String((message && message.content) || '').length,
          0,
        ),
        messageCount: snapshot.length,
        messageTypes: snapshot.reduce((counts, message) => {
          const type = String((message && message.type) || 'unknown');
          counts[type] = (counts[type] || 0) + 1;
          return counts;
        }, {}),
      };
    }

    function createPayloadDiagnostics(payload) {
      const fields = Array.isArray(payload && payload.fields) ? payload.fields : [];
      const fieldLengths = fields.map((field) => ({
        characterCount: String((field && field.value) || '').length,
        name: String((field && field.name) || ''),
      }));
      const summaryFieldNames = fieldLengths
        .map((field) => field.name)
        .filter((fieldName) => summaryFieldPattern.test(fieldName));

      return {
        destination: 'HubSpot Forms API',
        fieldCount: fieldLengths.length,
        fieldLengths,
        formId,
        payloadCharacterCount: JSON.stringify(payload || {}).length,
        portalId,
        summaryFieldCount: summaryFieldNames.length,
        summaryFieldNames,
        summaryTransportState:
          summaryFieldNames.length > 0 ? 'present' : 'absent',
        ...getConversationDiagnostics(),
      };
    }

    function addListener(element, eventName, listener) {
      if (!element || typeof element.addEventListener !== 'function') return;
      element.addEventListener(eventName, listener);
      listenerDisposers.push(() => {
        if (typeof element.removeEventListener === 'function') {
          element.removeEventListener(eventName, listener);
        }
      });
    }

    function showStatus(message) {
      if (!status) return;
      status.hidden = false;
      status.classList.add('is-error');
      status.textContent = message;
    }

    function clearErrors() {
      if (!formContainer) return;
      formContainer.querySelectorAll('.ai-chat__lead-error').forEach((element) => {
        element.textContent = '';
      });
      formContainer
        .querySelectorAll(
          '.ai-chat__lead-input, .ai-chat__lead-textarea, .ai-chat__lead-checkbox',
        )
        .forEach((element) => {
          element.classList.remove('is-error');
          element.setAttribute('aria-invalid', 'false');
        });
      if (status) {
        status.hidden = true;
        status.textContent = '';
        status.classList.remove('is-error');
      }
    }

    function setFieldError(fieldName, message) {
      if (!formContainer) return;
      const field = formContainer.querySelector(`[name="${fieldName}"]`);
      const error = formContainer.querySelector(`[data-error-for="${fieldName}"]`);
      if (field) {
        field.classList.add('is-error');
        field.setAttribute('aria-invalid', 'true');
      }
      if (error) error.textContent = message;
    }

    function readValues(formElement) {
      const formData = createFormData(formElement);
      return Object.freeze({
        consent: formData.get('consent') === 'true',
        email: String(formData.get('email') || '').trim(),
        firstname: String(formData.get('firstname') || '').trim(),
        lastname: String(formData.get('lastname') || '').trim(),
        phone: String(formData.get('phone') || '').trim(),
        phoneCountry: String(formData.get('phone_country') || '').trim(),
        zip: String(formData.get('zip') || '').trim(),
      });
    }

    function validate(formElement = form) {
      if (!formElement) return { isValid: false, values: null };
      const values = readValues(formElement);
      clearErrors();
      let isValid = true;

      if (!values.firstname) {
        setFieldError('firstname', fieldErrors.firstname);
        isValid = false;
      }
      if (!values.lastname) {
        setFieldError('lastname', fieldErrors.lastname);
        isValid = false;
      }
      if (!values.email) {
        setFieldError('email', fieldErrors.email);
        isValid = false;
      } else if (!core.isValidEmail(values.email)) {
        setFieldError('email', fieldErrors.emailInvalid);
        isValid = false;
      }
      if (!values.zip) {
        setFieldError('zip', fieldErrors.zip);
        isValid = false;
      }
      if (!values.consent) {
        setFieldError('consent', fieldErrors.consent);
        isValid = false;
      }

      if (!isValid) {
        const firstInvalidField = formContainer.querySelector('.is-error');
        if (firstInvalidField && typeof firstInvalidField.focus === 'function') {
          firstInvalidField.focus();
        }
      }

      return Object.freeze({ isValid, values });
    }

    function getCookieValue(name) {
      if (!name) return '';
      const cookiePrefix = `${name}=`;
      const cookies = String(documentRef.cookie || '').split(';');
      for (let index = 0; index < cookies.length; index += 1) {
        const cookie = cookies[index].trim();
        if (!cookie.startsWith(cookiePrefix)) continue;
        try {
          return decodeURIComponent(cookie.slice(cookiePrefix.length));
        } catch (_) {
          return '';
        }
      }
      return '';
    }

    function getSource() {
      const location = windowRef.location || {};
      const host = normalizeSourceDomain(location.hostname || location.href);
      // Shopify preview hosts use the store's primary domain for attribution.
      const shopDomain = normalizeSourceDomain(chatRoot.dataset.shopDomain);
      return sourceByDomain.get(host) || sourceByDomain.get(shopDomain) || host || shopDomain;
    }

    function buildFields(values) {
      const fields = [
        { objectTypeId: '0-1', name: 'firstname', value: values.firstname },
        { objectTypeId: '0-1', name: 'lastname', value: values.lastname },
        { objectTypeId: '0-1', name: 'email', value: values.email },
        { objectTypeId: '0-1', name: 'zip', value: values.zip },
        { objectTypeId: '0-1', name: 'mmax_channel', value: 'Web Lead' },
        { objectTypeId: '0-1', name: 'source', value: getSource() },
        { objectTypeId: '0-1', name: 'medium', value: 'Contact Sales' },
      ];

      if (boatBrand) {
        fields.push({ objectTypeId: '0-1', name: 'boatbrand', value: boatBrand });
      }
      if (boatModel) {
        fields.push({ objectTypeId: '0-1', name: 'boatmodel', value: boatModel });
      }
      if (values.phone) {
        fields.push({ objectTypeId: '0-1', name: 'phone', value: values.phone });
      }
      if (values.phoneCountry) {
        fields.push({
          objectTypeId: '0-1',
          name: 'phone_country',
          value: values.phoneCountry,
        });
      }
      if (values.consent) {
        fields.push({
          objectTypeId: '0-1',
          name: 'communications_consent',
          value: 'true',
        });
      }
      if (stockNumber) {
        fields.push({
          objectTypeId: '0-1',
          name: 'lastinterestedstocknumber',
          value: stockNumber,
        });
      }

      return fields;
    }

    function buildPayload(values) {
      const hubspotUtk = getCookieValue('hubspotutk');
      return {
        fields: buildFields(values),
        context: {
          ...(hubspotUtk ? { hutk: hubspotUtk } : {}),
          pageUri: windowRef.location.href,
          pageName: documentRef.title || productTitle || 'MarineMax AI Chat',
        },
      };
    }

    function setSubmitting(isSubmitting) {
      submitInFlight = Boolean(isSubmitting);
      if (form) form.setAttribute('aria-busy', String(submitInFlight));
      chatRoot.querySelectorAll('[data-ai-chat-form-submit]').forEach((button) => {
        button.disabled = submitInFlight;
        const label = button.querySelector('.btn__text');
        if (label) label.textContent = submitInFlight ? 'Sending...' : 'Submit';
        else button.textContent = submitInFlight ? 'Sending...' : 'Submit';
      });
    }

    function pushLeadEvent() {
      analytics.pushLeadEvents({
        form_name: 'ai_chat_contact',
        form_id: 'ai-chat-contact',
        item_name: productTitle || undefined,
        item_id: stockNumber || undefined,
        lead_type: 'ai_chat_contact',
        value: 100,
        currency: 'USD',
      });
    }

    async function submit(formElement = form) {
      if (!portalId || !formId) {
        showStatus(statusMessages.configuration);
        return false;
      }
      if (!formElement || submitInFlight) return false;

      const validation = validate(formElement);
      if (!validation.isValid) return false;
      setSubmitting(true);
      const payload = buildPayload(validation.values);
      const diagnostics = createPayloadDiagnostics(payload);
      const startedAt = Date.now();
      writeDiagnostics('submit:start', diagnostics);

      try {
        const response = await client.submit(portalId, formId, payload);
        writeDiagnostics('submit:success', {
          durationMs: Date.now() - startedAt,
          httpStatus: Number.isFinite(Number(response && response.status))
            ? Number(response.status)
            : null,
          summaryFieldCount: diagnostics.summaryFieldCount,
          summaryTransportState: diagnostics.summaryTransportState,
        });
        formElement.reset();
        syncPhoneMask();
        clearErrors();
        pushLeadEvent();
        navigation.showHubspotSuccess();
        return true;
      } catch (error) {
        writeDiagnostics(
          'submit:failure',
          {
            durationMs: Date.now() - startedAt,
            errorName: String((error && error.name) || 'Error'),
            errorMessage: String((error && error.message) || '').slice(0, 160),
            summaryFieldCount: diagnostics.summaryFieldCount,
            summaryTransportState: diagnostics.summaryTransportState,
          },
          'warn',
        );
        showStatus(statusMessages.submission);
        return false;
      } finally {
        setSubmitting(false);
      }
    }

    function setPhoneInputValueFromDigits(digits, config) {
      if (!phoneInput) return;
      phoneInput.value = digits ? config.formatter(digits) : '';
      phoneInput.placeholder = config.placeholder;
    }

    function syncPhoneMask() {
      if (!phoneInput || !phoneCountrySelect) return;
      const config = core.getPhoneMaskConfig(phoneCountrySelect.value);
      const digits = core.getPhoneDigits(phoneInput.value, config);
      setPhoneInputValueFromDigits(digits, config);
    }

    function handlePhoneKeydown(event) {
      if (
        (event.key !== 'Backspace' && event.key !== 'Delete') ||
        !phoneInput ||
        !phoneCountrySelect
      ) {
        return;
      }

      const selectionStart = phoneInput.selectionStart ?? 0;
      const selectionEnd = phoneInput.selectionEnd ?? selectionStart;
      if (selectionStart !== selectionEnd) return;

      const rawValue = phoneInput.value || '';
      const config = core.getPhoneMaskConfig(phoneCountrySelect.value);
      const digits = core.getPhoneDigits(rawValue, config);
      if (!digits) return;

      const targetIndex =
        event.key === 'Backspace'
          ? Math.max(0, selectionStart - 1)
          : Math.max(0, selectionStart);
      if (/\d/.test(rawValue.charAt(targetIndex))) return;

      const digitPosition =
        event.key === 'Backspace'
          ? core.getPhoneDigitsBeforeIndex(rawValue, selectionStart, config) - 1
          : core.getPhoneDigitsBeforeIndex(rawValue, selectionStart + 1, config) - 1;
      if (digitPosition < 0) {
        event.preventDefault();
        setPhoneInputValueFromDigits('', config);
        return;
      }

      const nextDigits =
        digits.slice(0, digitPosition) + digits.slice(digitPosition + 1);
      event.preventDefault();
      setPhoneInputValueFromDigits(nextDigits, config);
    }

    function setEmbedState(state) {
      if (!embed) return;
      const loading = state === 'loading';
      embed.classList[loading ? 'add' : 'remove']('is-loading');
      embed.classList[state === 'error' ? 'add' : 'remove']('has-load-error');
      embed.setAttribute('aria-busy', String(loading));
      if (embedLoader) embedLoader.hidden = !loading;
      if (status && state !== 'error') {
        status.hidden = true;
        status.textContent = '';
        status.classList.remove('is-error');
      }
    }

    // Listen before loading HubSpot to avoid missing its ready event.
    function handleEmbedReady(event) {
      if (!embed || !event.detail || event.detail.formId !== formId) return;
      const api = windowRef.HubSpotFormsV4;
      if (!api) return;
      // A configured instance ID keeps multiple PDP widgets isolated.
      const frame = embed.querySelector('.hs-form-frame');
      if (!frame || frame.dataset.instanceId !== event.detail.instanceId) return;
      const instance = api.getFormFromEvent(event);
      embedInstanceId = event.detail.instanceId;
      const fields = {
        lastinterestedstocknumber: stockNumber,
        boatmodel: boatModel,
        boatbrand: boatBrand,
        mmax_channel: 'Web Lead',
        source: getSource(),
        medium: 'Contact Sales',
      };
      Object.entries(fields).forEach(([name, value]) => {
        if (value) instance.setFieldValue(`0-1/${name}`, value);
      });
      if (embedTimer) windowRef.clearTimeout(embedTimer);
      embedTimer = null;
      setEmbedState('ready');
    }

    function handleEmbedSuccess(event) {
      if (!embed || !event.detail || event.detail.formId !== formId || event.detail.instanceId !== embedInstanceId || embedCompleted) return;
      embedCompleted = true;
      pushLeadEvent();
      navigation.showHubspotSuccess({ native: true });
    }

    function resetSubmittedEmbed() {
      if (!embed || !embedCompleted) return;
      const frame = embed.querySelector('.hs-form-frame');
      if (!frame) return;

      // A new standard embed lets HubSpot rebuild its form and CAPTCHA.
      const freshFrame = documentRef.createElement('div');
      freshFrame.className = 'hs-form-frame';
      freshFrame.dataset.region = region;
      freshFrame.dataset.portalId = portalId;
      freshFrame.dataset.formId = formId;
      freshFrame.dataset.instanceId = `${embedBaseInstanceId}-${++embedRestartCount}`;

      if (embedTimer) windowRef.clearTimeout(embedTimer);
      embedTimer = null;
      embedInstanceId = null;
      embedCompleted = false;
      embedStarted = false;
      setEmbedState('loading');
      frame.replaceWith(freshFrame);
    }

    function loadEmbed() {
      if (!embed || embedStarted || embedInstanceId) return;
      embedStarted = true;
      setEmbedState('loading');
      embedTimer = windowRef.setTimeout(() => {
        embedTimer = null;
        if (!embedInstanceId) {
          setEmbedState('error');
          showStatus('The contact form is taking longer to load. Please reload the page and try again.');
        }
      }, 20000);
      const embedHost = region === 'na1' ? 'js.hsforms.net' : `js-${region}.hsforms.net`;
      const src = `https://${embedHost}/forms/embed/${encodeURIComponent(portalId)}.js`;
      // The portal loader discovers all hs-form-frame elements on the page.
      if (!documentRef.querySelector(`script[src="${src}"]`)) {
        const script = documentRef.createElement('script');
        script.src = src;
        script.async = true;
        script.addEventListener('error', () => {
          embedStarted = false;
          if (embedTimer) windowRef.clearTimeout(embedTimer);
          embedTimer = null;
          script.remove();
          setEmbedState('error');
          showStatus('Unable to load the contact form. Please reopen it to try again.');
        });
        documentRef.head.appendChild(script);
      }
    }

    function show(options = {}) {
      returnTo = options.returnTo === 'pdp' ? 'pdp' : 'chat';
      navigation.showExpert(options);
      resetSubmittedEmbed();
      loadEmbed();
    }

    function returnFromForm() {
      if (isDesktopFormOnly()) return false;
      if (returnTo === 'pdp' && onReturnToPdp() !== false) return true;
      navigation.showChat();
      return true;
    }

    function continueAfterSuccess() {
      if (!returnFromForm()) return false;
      conversation.setConversationId(null);
      messages.clear();
      if (input) {
        input.placeholder = defaultInputPlaceholder;
        resizeInput({ reset: true });
      }
      return true;
    }

    function syncDesktopFormOnlyMode() {
      const shouldUseFormOnly = isDesktopFormOnly();
      if (shouldUseFormOnly) {
        if (!desktopFormOnlyWasActive) show({ savePreviousState: false });
        desktopFormOnlyWasActive = true;
        return;
      }

      if (desktopFormOnlyWasActive) {
        desktopFormOnlyWasActive = false;
        navigation.showChat({ allowDesktopFormExit: true });
      }
    }

    function initialize() {
      if (initialized) return false;
      initialized = true;

      addListener(phoneCountrySelect, 'change', syncPhoneMask);
      addListener(phoneInput, 'keydown', handlePhoneKeydown);
      addListener(phoneInput, 'input', syncPhoneMask);
      addListener(backToChatButton, 'click', (event) => {
        event.preventDefault();
        returnFromForm();
      });
      addListener(continueButton, 'click', (event) => {
        event.preventDefault();
        continueAfterSuccess();
      });
      addListener(stickySubmit, 'click', () => {
        if (!form) return;
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else if (EventClass) {
          form.dispatchEvent(new EventClass('submit', { bubbles: true, cancelable: true }));
        }
      });
      addListener(form, 'submit', async (event) => {
        event.preventDefault();
        try {
          await submit(form);
        } catch (_) {}
      });
      if (form) form.dataset.bound = 'true';

      addListener(windowRef, 'hs-form-event:on-ready', handleEmbedReady);
      addListener(windowRef, 'hs-form-event:on-submission:success', handleEmbedSuccess);
      if (embed && windowRef.HubSpotFormsV4) {
        windowRef.HubSpotFormsV4.getForms().forEach((instance) => {
          handleEmbedReady({ detail: { formId: instance.getFormId(), instanceId: instance.getInstanceId() } });
        });
      }
      syncPhoneMask();
      syncDesktopFormOnlyMode();
      if (matchMedia) {
        const desktopMedia = matchMedia('(min-width: 1025px)');
        if (typeof desktopMedia.addEventListener === 'function') {
          addListener(desktopMedia, 'change', syncDesktopFormOnlyMode);
        } else if (typeof desktopMedia.addListener === 'function') {
          desktopMedia.addListener(syncDesktopFormOnlyMode);
          listenerDisposers.push(() => {
            if (typeof desktopMedia.removeListener === 'function') {
              desktopMedia.removeListener(syncDesktopFormOnlyMode);
            }
          });
        }
      }
      return true;
    }

    function destroy() {
      if (embedTimer) windowRef.clearTimeout(embedTimer);
      listenerDisposers.splice(0).forEach((dispose) => dispose());
      if (form) delete form.dataset.bound;
      initialized = false;
      submitInFlight = false;
    }

    return Object.freeze({
      clearErrors,
      destroy,
      initialize,
      show,
    });
  }

  return Object.freeze({
    createHubSpotFormController,
    fieldErrors,
    statusMessages,
  });
});
